import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  fetchFlavorFormulas,
  type AmountUnit,
  type FlavorFormulaDto,
  type ListFormulasResponse,
} from "@/services/formula_api";
import { registerServerCompounds, registerServerLens } from "@/utils/serverCompounds";
import { normalizeAmountUnit } from "@/utils/formulation/normalizeAmountUnit";
import type { Variant } from "@/utils/types/variant";
import type { FormulationEntry } from "@/utils/types/compound";

// Cache key family — kept alongside the existing flavor query keys in
// useSidebarFlavors / useFlavorSession. Importing those there would create a
// cycle, so we declare a tiny local family here.
export const flavorFormulaKeys = {
  all: ["flavorFormulas"] as const,
  byFlavor: (id: string) => ["flavorFormulas", id] as const,
};

function dtoToVariant(dto: FlavorFormulaDto): Variant {
  // Split items by category. FLAVOR rows feed the existing `formulation`
  // (the FlaskPanel "FLAVOR" section); FIXED_INGREDIENT rows feed the new
  // `fixedIngredients` (the FlaskPanel "Fixed ingredients" / `foundation`
  // prop section). FormulationEntry has a required ppm field, so for items
  // with NULL amount we use 0 as a placeholder — FlaskRow renders this as a
  // flat slider, which is the right "concentration not decided yet" cue.
  // The autosave hook re-attaches the original null when writing back.
  const formulation: FormulationEntry[] = [];
  const fixedIngredients: FormulationEntry[] = [];
  for (const it of dto.items) {
    // amountUnit comes from public.flavor_formula_version_item.amount_unit;
    // null on rows where the amount is also TBD (FIXED_INGREDIENT placeholders).
    // Default to "ppm" only when the server has no recorded unit.
    //
    // Normalize the (value, unit) pair on read so the initial render shows
    // the value in the most readable unit in its chain (ppm/ppb/ppt, mg/g/kg,
    // μL/mL/L). The autosave path also normalizes on save, so the DB will
    // eventually settle on the canonical form — but normalizing on read too
    // means old rows saved before this lands display nicely on first paint.
    const rawValue = it.amountValue ?? 0;
    const rawUnit: AmountUnit = it.amountUnit ?? "ppm";
    const norm = normalizeAmountUnit(rawValue, rawUnit);
    const entry: FormulationEntry = {
      compoundId: it.compoundId,
      ppm: norm.value,
      amountUnit: norm.unit,
    };
    if (it.category === "FLAVOR") formulation.push(entry);
    else if (it.category === "FIXED_INGREDIENT") fixedIngredients.push(entry);
    // Unknown categories are intentionally dropped — the type union is
    // exhaustive today, but this prevents silent misrouting if the server
    // adds a new category in the future.
  }
  return {
    id: `f${dto.formulaNumber}`,                    // tab-stable id
    name: dto.name,
    formulation,
    fixedIngredients,
    prompt: dto.prompt,
    // `|| Date.now()` falls back when the server format ever changes and the
    // parse yields NaN — otherwise NaN silently corrupts tab sort order.
    createdAt: new Date(dto.createdAt).getTime() || Date.now(),
    formulaId: dto.id,
    formulaNumber: dto.formulaNumber,
    currentVersionId: dto.currentVersionId,
  };
}
// AUTOSAVE CONTRACT: when building SaveVersionItem[]s from a Variant,
// FIXED_INGREDIENT entries where ppm === 0 must be written back as
// { amountValue: null, amountUnit: null } — the 0 above is a placeholder
// for "concentration not decided yet". FLAVOR entries always carry a real
// server-provided amount; do not apply this rule to them.

export function useFlavorFormulas(flavorId: string | null) {
  const q = useQuery({
    queryKey: flavorId !== null
      ? flavorFormulaKeys.byFlavor(flavorId)
      : flavorFormulaKeys.byFlavor("__none__"),
    queryFn: ({ signal }) => fetchFlavorFormulas(flavorId!, signal),
    enabled: flavorId !== null,
    staleTime: 30_000,
  });

  // Side-effect: every time fresh data lands, prime the server-compounds
  // registry so FlaskPanel/FlaskRow's compoundById fallback can resolve
  // these ids on the next render.
  useEffect(() => {
    if (!q.data) return;
    const allItems = q.data.formulas.flatMap((f) => f.items);
    if (allItems.length > 0) {
      registerServerCompounds(allItems);
      registerServerLens(
        allItems.map((it) => ({ compoundId: it.compoundId, lens: it.lens })),
      );
    }
  }, [q.data]);

  return {
    ...q,
    variants:
      q.data?.formulas.map(dtoToVariant) ?? null,
  };
}

export type UseFlavorFormulasResult = ReturnType<typeof useFlavorFormulas>;

// Re-export the wire type so call sites can pass it across hook boundaries
// without importing from formula_api.
export type { ListFormulasResponse };
